import { z } from 'zod';

import { CommonAddressSchema } from './addressSchema.ts';
import {
  createPaginatedResponseSchema,
  PagedRequestSchema,
} from './pagingSchema.ts';
import { FleetStatusEnum } from './statusSchema.ts';
import { VehicleSchema } from './vehicleSchema.ts';

export const FleetAccountTypeEnum = z.enum(['Fleet', 'Commercial']);

export const FleetCompanyAddressSchema = z.object({
  addressLine1: z.string().nullish(),
  postalCode: z.string().nullish(),
  addressLine2: z.string().nullish(),
  addressLine3: z.string().nullish(),
  addressLine4: z.string().nullish(),
  country: z.string().nullish(),
  countryCode: z.string().nullish(),
  timeZone: z.string().nullish(),
  geoLocation: z
    .object({
      lat: z.string().nullish(),
      lng: z.string().nullish(),
    })
    .nullish(),
});

export const FleetCompanyDetailsSchema = z.object({
  emailAddress: z.string(),
  phoneNumber: z.string(),
  address: FleetCompanyAddressSchema.nullish(),
});

const AddFleetCompanyDetailsSchema = z.object({
  emailAddress: z.string().nullish(),
  phoneNumber: z.string().nullish(),
  address: FleetCompanyAddressSchema,
});

const FleetContactSchema = z.object({
  isPrimary: z.boolean(),
  position: z.string().nullish(),
  title: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  initial: z.string().nullish(),
  emailAddress: z.string().nullish(),
  phoneNumber: z.string().nullish(),
});

export const FleetContactUpdateResponseSchema = z.object({
  fleetAccountId: z.string(),
  contactId: z.string(),
});

export const CreateFleetContactSchema = FleetContactSchema.extend({
  fleetAccountId: z.uuid(),
});

export const UpdateFleetContactSchema = CreateFleetContactSchema.extend({
  contactId: z.uuid(),
});

export const AddFleetAccountRequestSchema = z.object({
  accountNumber: z.string().nullish(),
  companyName: z.string().nullish(),
  type: FleetAccountTypeEnum,
  companyDetails: AddFleetCompanyDetailsSchema,
  status: FleetStatusEnum,
  agreedCreditLimit: z.number().nullish(),
  creditAvailable: z.number().nullish(),
  contacts: z.array(FleetContactSchema).nullish(),
  authorisation: z.object({
    alwaysRequired: z.boolean(),
    autoAuthoriseOnJobValueLessThan: z.int(),
  }),
});

export const UpdateFleetAccountSchema = z.object({
  fleetAccountId: z.uuid(),
  companyName: z.string().nullish(),
  companyDetails: FleetCompanyDetailsSchema,
});

export const FleetAccountIdSchema = z.object({
  fleetAccountId: z.uuid(),
});

export const FleetAccountSearchResultItemSchema = z.object({
  fleetAccountId: z.uuid(),
  accountNumber: z.string(),
  companyName: z.string().nullish(),
  status: z.string().nullish(),
  creditLimit: z.number(),
  creditAvailable: z.number(),
  postCode: z.string().nullish(),
  email: z.string().nullish(),
  address: z.string().nullish(),
  parentCompanyName: z.string().nullish(),
  customerId: z.string().optional(),
  customerReference: z.string().nullish(),
});

export const FleetStatusSchema = z.object({
  status: FleetStatusEnum,
  isCurrent: z.boolean(),
});

export const UpdateFleetStatusSchema = z.object({
  newStatus: FleetStatusEnum,
});

export const UpdateFleetStatusRequestSchema = z.object({
  fleetAccountId: z.uuid(),
  status: FleetStatusEnum,
});

export const FleetCreditLimitSchema = z.object({
  agreedCreditLimit: z.number().nonnegative(),
  isCurrent: z.boolean(),
});

export const UpdateFleetCreditLimitSchema = z.object({
  fleetAccountId: z.uuid(),
  creditLimit: z.number(),
});

export const FleetAuthorisationSchema = z.object({
  alwaysRequired: z.boolean(),
  autoAuthoriseOnJobValueLessThan: z.number(),
});

export const FleetAccountSchema = z.object({
  id: z.uuid(),
  accountNumber: z.string(),
  companyName: z.string(),
  type: FleetAccountTypeEnum.optional(),
  externalId: z.string().nullish(),
  companyDetails: FleetCompanyDetailsSchema.nullish(),
  creditLimit: z.number().nullish(),
  creditAvailable: z.number().nullish(),
  authorisation: FleetAuthorisationSchema.nullish(),
  status: FleetStatusEnum.optional(),
});

export const GetFleetAccountByIdResponseSchema = z.object({
  fleetAccountId: z.uuid(),
  accountNumber: z.string().nullish(),
  companyName: z.string().nullish(),
  companyDetails: FleetCompanyDetailsSchema.omit({ address: true }).extend({
    address: CommonAddressSchema.nullish(),
  }),
  status: z.string().nullish(),
  creditLimit: z.number(),
  creditAvailable: z.number(),
  contacts: z.array(UpdateFleetContactSchema).nullish(),
  authorisation: FleetAuthorisationSchema.nullish(),
  dateAdded: z.string(),
});

const FleetAccountFormCompanyDetailsSchema = z.object({
  emailAddress: z.email(),
  phoneNumber: z.string(),
  address: FleetCompanyAddressSchema.omit({
    addressLine1: true,
    postalCode: true,
  }).extend({
    addressLine1: z.string().min(2),
    postalCode: z.string().min(3),
  }),
});

export const FleetAccountFormSchema = FleetAccountSchema.omit({
  id: true,
  accountNumber: true,
  companyName: true,
  type: true,
  authorisation: true,
  status: true,
  companyDetails: true,
}).extend({
  accountNumber: z.string().min(3),
  companyName: z.string().min(3),
  type: FleetAccountTypeEnum,
  companyDetails: FleetAccountFormCompanyDetailsSchema,
  authorisation: FleetAuthorisationSchema,
  status: FleetStatusEnum,
});

export const FleetSearchRequestSchema = z
  .object({
    searchTerm: z
      .union([
        z
          .string()
          .min(2)
          .regex(/^[A-Za-z0-9, ]+$/),
        z.literal(''),
      ])
      .optional(),
    address: z
      .union([
        z
          .string()
          .min(2)
          .regex(/^[A-Za-z0-9, ]+$/),
        z.literal(''),
      ])
      .optional(),
  })
  .extend(PagedRequestSchema.shape);

export const FleetSearchResultSchema = z.object({
  fleetAccountId: z.uuid(),
  accountNumber: z.string(),
  companyName: z.string().nullish(),
  status: z.string().nullish(),
  creditLimit: z.number(),
  creditAvailable: z.number(),
  postCode: z.string().nullish(),
  email: z.string().nullish(),
  address: z.string().nullish(),
  parentCompanyName: z.string().nullish(),
  customerId: z.string().optional(),
  customerReference: z.string().nullish(),
});

export const FleetSearchResponseSchema = createPaginatedResponseSchema(
  FleetSearchResultSchema
);

export const BridgestoneFleetSearchRequestSchema = z.object({
  page: z.number().int(),
  pageSize: z.number().int(),
  criteria: z.array(
    z.object({
      searchText: z.union([
        z
          .string()
          .min(2)
          .regex(/^[A-Za-z0-9, ]+$/),
        z.literal(''),
      ]),
      searchType: z.enum(['Address', 'Name']),
    })
  ),
  serviceLocationId: z.uuid(),
});

export const BridgestoneFleetSearchResponseSchema = z.object({
  data: z.array(FleetSearchResultSchema).nullish(),
});

export const FleetLicenceSearchRequestSchema = z.object({
  licencePlate: z.string(),
  state: z.string(),
  accountNumber: z.string().optional(),
  driverId: z.string().optional(),
});

export const FleetLicenceSearchResponseSchema = createPaginatedResponseSchema(
  VehicleSchema.omit({ mileage: true })
);

export const ValidateFleetRequestSchema = z.object({
  fleetId: z.string(),
  serviceLocationId: z.uuid(),
});

export const ValidateFleetSchema = z.object({
  fleet: FleetAccountSearchResultItemSchema.nullish(),
  serviceLocationId: z.uuid(),
});

export const ValidateFleetResponseSchema = z.object({
  success: z.boolean(),
  header: z.string().nullish(),
  message: z.string().nullish(),
  customerInstruction: z.array(z.string()).nullish(),
  customerId: z.string().nullish(),
  customerReference: z.string().nullish(),
});

export const FleetContactCreateEditSchema = z.object({
  fleetAccountId: z.uuid(),
  isPrimary: z.boolean(),
  position: z.string().optional(),
  title: z.string().optional(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  initial: z.string().optional(),
  emailAddress: z.email().min(1),
  phoneNumber: z.string().min(1),
});

export const FleetDriverVehiclesRequestSchema = z.object({
  driverId: z.uuid().optional(),
  fleetAccountNumber: z.string(),
  pageNumber: z.int(),
  resultsPerPage: z.int(),
});

export const SearchFleetDriverVehiclesFormSchema = z.object({
  licencePlate: z.string().min(1),
  state: z.string().min(1),
  vin: z.string().optional(),
});

export const SearchFleetDriverVehiclesRequestSchema =
  SearchFleetDriverVehiclesFormSchema.extend({
    driverID: z.uuid(),
    pageNumber: z.int(),
    pageSize: z.int(),
    accountNumber: z.string(),
  });

export const FleetDriverVehicleSchema = z.object({
  id: z.uuid(),
  fulfilmentId: z.string().optional(),
  vrn: z.string().optional(),
  vin: z.string().nullish(),
  type: z.string().nullish(),
  fuelType: z.string().nullish(),
  year: z.number(),
  make: z.string().nullish(),
  makeId: z.string().optional(),
  model: z.string().nullish(),
  modelId: z.string().optional(),
  variant: z.string().nullish(),
  variantId: z.string().optional(),
  description: z.string().nullish(),
  color: z.string().nullish(),
  axleCode: z.string().nullish(),
  engineSize: z.string().nullish(),
  engineCode: z.string().nullish(),
  recordings: z
    .array(
      z.object({
        id: z.uuid(),
        recordingType: z.string().nullish(),
        value: z.string().nullish(),
      })
    )
    .nullish(),
  validFrom: z.string(),
  validTo: z.string(),
  mileage: z.number().optional(),
  dateOfLastService: z.string().nullish(),
  isValid: z.boolean(),
  driverId: z.uuid().optional(),
  license: z.string().nullish(),
  state: z.string().optional(),
  accountNumber: z.string().nullish(),
  isRetained: z.boolean().optional(),
});

export const FleetDriverVehiclesResponseSchema = createPaginatedResponseSchema(
  FleetDriverVehicleSchema
);

export const FleetCustomerSearchRequestSchema = z.object({
  searchText: z.string(),
});

export const FleetCustomerSearchResultSchema = z.object({
  customerId: z.string().optional(),
  customerReference: z.string().optional(),
  firstName: z.string(),
  lastName: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().optional(),
  postcode: z.string().optional(),
});

export const FleetCustomerSearchResponseSchema = createPaginatedResponseSchema(
  FleetCustomerSearchResultSchema
);
export const FleetDeliveryReceiptSetupRequestSchema = z.object({
  useCache: z.boolean(),
  dealerId: z.string().nullish(),
  fleet: z.string(),
  tireOnlySale: z.boolean(),
  serviceDoneOnVehicle: z.boolean(),
  isReturnApplied: z.boolean(),
  isOffTheRoad: z.boolean(),
  priceBook: z.string().nullable(),
});

export const FleetDeliveryReceiptSetupItemSchema = z.object({
  fieldName: z.string().optional(),
  maxlength: z.string().optional(),
  mandatory: z.enum(['true', 'false']).optional(),
  width: z.string().optional(),
  validation: z.string().optional(),
  thresholds: z
    .array(
      z.object({
        limit: z.enum(['dollar', 'quantity', 'Quantity']),
        type: z.enum(['ServicePartsLabor', 'Tire', 'All']),
        value: z.string().optional(),
      })
    )
    .optional(),
  mask: z
    .array(
      z
        .object({
          regex: z.string().optional(),
          pattern: z.string().optional(),
        })
        .optional()
    )
    .optional(),
});

export const FleetDeliveryReceiptSetupResponseSchema = z.object({
  fields: z.array(FleetDeliveryReceiptSetupItemSchema).optional(),
});

export const FleetBillingDetailsRequestSchema = z.object({
  fleetId: z.string(),
  serviceLocationId: z.uuid(),
});

export const FleetBillingDetailsResponseSchema = z.object({
  lastUpdated: z.string().nullish(),
  billingDetails: z
    .object({
      // null or empty string indicates AI (Auto Integrate) not enabled
      aiEnabled: z.string().nullish(),
      creditReleaseQtyStr: z.string().optional(),
      creditReleaseThresholdAmount: z.string().optional(),
    })
    .nullish(),
});

export const FleetBillingDetailsFeSchema = z.object({
  purchaseOrderNumber: z.string().nullish(),
  vehicleNumber: z.string().nullish(),
  programNumber: z.string().nullish(),
  branchDivision: z.string().nullish(),
  secondPurchaseOrderNumber: z.string().nullish(),
  approversName: z.string().nullish(),
  companyEmployeeId: z.string().nullish(),
  driversLicenseNumber: z.string().nullish(),
  driversLocation: z.string().nullish(),
  trailerNumber: z.string().nullish(),
  hubometerReading: z.string().nullish(),
  associateDealerName: z.string().nullish(),
  billingRequirementsValid: z.string().nullish(),
});

export const FleetCreditReleaseSchema = z.object({
  creditReleaseNumber: z.string().optional(),
});

export const FleetDeductibleSchema = z.object({
  deductibleAmount: z.number(),
});

export const CreateFleetOrderSchema = z.object({
  customer: z.object({
    customerType: z.string(),
    customerId: z.string(),
    customerReference: z.string(),
    customerName: z.object({
      firstName: z.string(),
      lastName: z.string(),
    }),
    fleetAccount: z.object({
      accountNumber: z.string(),
      companyName: z.string(),
      parentCompanyName: z.string(),
    }),
    source: z.object({
      fulfilmentBaseLocation: z.string(),
      customerSourcePrimary: z.string(),
    }),
  }),
  fulfilmentTo: z.object({
    id: z.string(),
    dateCreated: z.string(),
  }),
  source: z.object({
    fulfilmentBaseLocation: z.string(),
  }),
  status: z.string(),
});

export const CreateFleetOrderResponseSchema = z.object({
  orderId: z.string(),
  orderNumber: z.string(),
});

export const FleetDetailsSchema = z.object({
  accountNumber: z.string().optional(),
  accountName: z.string().optional(),
  Name: z.string().optional(),
  accountAddress: z.string().optional(),
});

export type GetFleetAccountByIdResponseModel = z.infer<
  typeof GetFleetAccountByIdResponseSchema
>;
export type FleetAccountModel = z.infer<typeof FleetAccountSchema>;
export type FleetAccountFormModel = z.infer<typeof FleetAccountFormSchema>;
export type FleetDetailsModel = z.infer<typeof FleetDetailsSchema>;
export type FleetSearchRequestModel = z.infer<typeof FleetSearchRequestSchema>;
export type FleetSearchResponseModel = z.infer<
  typeof FleetSearchResponseSchema
>;
export type FleetLicenceSearchRequestModel = z.infer<
  typeof FleetLicenceSearchRequestSchema
>;
export type BridgestoneFleetSearchRequestModel = z.infer<
  typeof BridgestoneFleetSearchRequestSchema
>;
export type BridgestoneFleetSearchResponseModel = z.infer<
  typeof BridgestoneFleetSearchResponseSchema
>;
export type FleetLicenceSearchResponseModel = z.infer<
  typeof FleetLicenceSearchResponseSchema
>;
export type FleetContactUpdateResponseModel = z.infer<
  typeof FleetContactUpdateResponseSchema
>;
export type FleetContactCreateEditModel = z.infer<
  typeof FleetContactCreateEditSchema
>;
export type FleetDriverVehiclesRequestModel = z.infer<
  typeof FleetDriverVehiclesRequestSchema
>;
export type SearchFleetDriverVehiclesFormModel = z.infer<
  typeof SearchFleetDriverVehiclesFormSchema
>;
export type SearchFleetDriverVehiclesRequestModel = z.infer<
  typeof SearchFleetDriverVehiclesRequestSchema
>;
export type FleetDriverVehiclesResponseModel = z.infer<
  typeof FleetDriverVehiclesResponseSchema
>;
export type FleetCustomerSearchRequestModel = z.infer<
  typeof FleetCustomerSearchRequestSchema
>;

export type FleetCustomerSearchResponseModel = z.infer<
  typeof FleetCustomerSearchResponseSchema
>;

export type FleetCustomerSearchResultModel = z.infer<
  typeof FleetCustomerSearchResultSchema
>;

//Status
export type UpdateFleetStatusModel = z.infer<typeof UpdateFleetStatusSchema>;
export type UpdateFleetStatusRequestModel = z.infer<
  typeof UpdateFleetStatusRequestSchema
>;

//Validate

export type ValidateFleetRequestModel = z.infer<
  typeof ValidateFleetRequestSchema
>;
export type ValidateFleetResponseModel = z.infer<
  typeof ValidateFleetResponseSchema
>;
export type ValidateFleetModel = z.infer<typeof ValidateFleetSchema>;
export type FleetDeliveryReceiptSetupRequestModel = z.infer<
  typeof FleetDeliveryReceiptSetupRequestSchema
>;
export type FleetDeliveryReceiptSetupResponseModel = z.infer<
  typeof FleetDeliveryReceiptSetupResponseSchema
>;
export type FleetBillingDetailsFeModel = z.infer<
  typeof FleetBillingDetailsFeSchema
>;
export type UpdateFleetCreditLimitModel = z.infer<
  typeof UpdateFleetCreditLimitSchema
>;
export type FleetCreditReleaseModel = z.infer<typeof FleetCreditReleaseSchema>;
export type FleetDeductibleModel = z.infer<typeof FleetDeductibleSchema>;
export type FleetDeliveryReceiptSetupItemModel = z.infer<
  typeof FleetDeliveryReceiptSetupItemSchema
>;
export type FleetBillingDetailsRequestModel = z.infer<
  typeof FleetBillingDetailsRequestSchema
>;
export type FleetBillingDetailsResponseModel = z.infer<
  typeof FleetBillingDetailsResponseSchema
>;

//Create

export type CreateFleetOrderModel = z.infer<typeof CreateFleetOrderSchema>;

export type CreateFleetOrderResponseModel = z.infer<
  typeof CreateFleetOrderResponseSchema
>;
export type FleetAccountIdModel = z.infer<typeof FleetAccountIdSchema>;
export type AddFleetAccountRequestModel = z.infer<
  typeof AddFleetAccountRequestSchema
>;
export type UpdateFleetAccountModel = z.infer<typeof UpdateFleetAccountSchema>;

export type CreateFleetContactModel = z.infer<typeof CreateFleetContactSchema>;
export type UpdateFleetContactModel = z.infer<typeof UpdateFleetContactSchema>;
export type FleetAccountSearchResultItemModel = z.infer<
  typeof FleetAccountSearchResultItemSchema
>;
